"use client";

import { useCallback, useEffect, useState } from "react";

type JobName = "top20" | "below20" | "daily" | "snapshot" | "market-ticker-warm";

type StatusPayload = {
  ok?: boolean;
  enabled?: boolean;
  runs?: Array<{
    id: string;
    runType: string;
    status: string;
    startedAt: string;
    finishedAt: string | null;
    startupsSeen: number;
    startupsInserted: number;
    startupsUpdated: number;
    versionsInserted: number;
    metadata?: Record<string, unknown>;
  }>;
  counts?: {
    trustmrr_startups: number;
    trustmrr_startup_versions: number;
    trustmrr_category_sync_state: number;
    trustmrr_sync_runs: number;
    niche_daily_snapshots: number;
    market_ticker_daily_cache?: number;
  };
  error?: string;
  details?: string;
};

type JobPayload = {
  ok?: boolean;
  job?: JobName;
  result?: Record<string, unknown>;
  error?: string;
  details?: string;
};

const JOBS: Array<{ key: JobName; label: string }> = [
  { key: "top20", label: "Run Top20 Bootstrap" },
  { key: "below20", label: "Run Below20 Bootstrap" },
  { key: "daily", label: "Run Daily Refresh" },
  { key: "snapshot", label: "Run Snapshot" },
  { key: "market-ticker-warm", label: "Warm Market Ticker" }
];

function formatHeartbeat(iso?: string): string | null {
  if (!iso) return null;
  const ts = Date.parse(iso);
  if (!Number.isFinite(ts)) return null;
  const deltaSeconds = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (deltaSeconds < 60) return `${deltaSeconds}s ago`;
  const deltaMinutes = Math.round(deltaSeconds / 60);
  if (deltaMinutes < 60) return `${deltaMinutes}m ago`;
  const deltaHours = Math.round(deltaMinutes / 60);
  return `${deltaHours}h ago`;
}

export function JobConsoleOverlay() {
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<StatusPayload | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const hasActiveRun = Boolean(
    status?.runs?.some((run) => {
      const metadata = run.metadata ?? {};
      const abortRequested = Boolean((metadata as { abort_requested?: boolean }).abort_requested);
      return run.status === "running" || run.status === "stopping" || abortRequested;
    })
  );

  const refreshStatus = useCallback(async () => {
    try {
      const response = await fetch(`/api/job-console?_t=${Date.now()}`, {
        cache: "no-store",
        headers: { Pragma: "no-cache", "Cache-Control": "no-cache" }
      });
      const payload = (await response.json()) as StatusPayload;
      if (!response.ok) {
        throw new Error(payload.details ?? payload.error ?? "Failed to load status.");
      }
      setStatus(payload);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Failed to load status.");
    }
  }, []);

  const runJob = useCallback(async (job: JobName) => {
    try {
      setMessage(null);
      await refreshStatus();
      const response = await fetch("/api/job-console", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ job })
      });
      const payload = (await response.json()) as JobPayload;
      if (!response.ok || !payload.ok) {
        throw new Error(payload.details ?? payload.error ?? "Job failed.");
      }
      setMessage(`${job} completed.`);
      await refreshStatus();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Job failed.");
    }
  }, [refreshStatus]);

  const stopJob = useCallback(async (runId: string) => {
    try {
      const response = await fetch("/api/job-console", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ job: "stop", runId })
      });
      const payload = (await response.json()) as JobPayload;
      if (!response.ok || !payload.ok) {
        throw new Error(payload.details ?? payload.error ?? "Stop failed.");
      }
      setMessage("Stop requested. Job will finish current step and stop.");
      await refreshStatus();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Stop failed.");
    }
  }, [refreshStatus]);

  useEffect(() => {
    if (process.env.NODE_ENV !== "development") {
      setEnabled(false);
      return;
    }

    const host = window.location.hostname;
    const local = host === "localhost" || host === "127.0.0.1" || host === "::1";
    setEnabled(local);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    if (!open) return;
    let cancelled = false;
    const intervalMs = hasActiveRun ? 1000 : 4000;
    const tick = async () => {
      if (cancelled) return;
      await refreshStatus();
    };
    tick();
    const interval = setInterval(tick, intervalMs);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [enabled, open, hasActiveRun, refreshStatus]);

  if (!enabled) {
    return null;
  }

  return (
    <div className="job-console-overlay">
      <button
        type="button"
        className="btn btn--primary btn--sm"
        onClick={() => setOpen((prev) => !prev)}
      >
        {open ? "Close Jobs" : "Data Jobs"}
      </button>

      {open && (
        <div className="job-console-panel">
          <p className="section-label">Mirror Job Console</p>
          <div className="job-console-actions">
            {JOBS.map((job) => (
              <button
                key={job.key}
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => runJob(job.key)}
              >
                {job.label}
              </button>
            ))}
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={refreshStatus}
            >
              Refresh Status
            </button>
          </div>

          {message && <p className="job-console-message">{message}</p>}

          {status?.counts && (
            <div className="job-console-stats">
              <span className="detail-pill">Startups: {status.counts.trustmrr_startups}</span>
              <span className="detail-pill">Versions: {status.counts.trustmrr_startup_versions}</span>
              <span className="detail-pill">Sync Runs: {status.counts.trustmrr_sync_runs}</span>
              <span className="detail-pill">Snapshots: {status.counts.niche_daily_snapshots}</span>
              <span className="detail-pill">Ticker: {status.counts.market_ticker_daily_cache ?? 0}</span>
            </div>
          )}

          {status?.runs && status.runs.length > 0 && (
            <div className="job-console-runs">
              {status.runs.slice(0, 6).map((run) => {
                const metadata = run.metadata ?? {};
                const abortRequested = Boolean((metadata as { abort_requested?: boolean }).abort_requested);
                const phase = typeof (metadata as { phase?: unknown }).phase === "string"
                  ? String((metadata as { phase?: unknown }).phase)
                  : null;
                const currentCategory = typeof (metadata as { currentCategory?: unknown }).currentCategory === "string"
                  ? String((metadata as { currentCategory?: unknown }).currentCategory)
                  : null;
                const currentSort = typeof (metadata as { currentSort?: unknown }).currentSort === "string"
                  ? String((metadata as { currentSort?: unknown }).currentSort)
                  : null;
                const currentPageRaw = (metadata as { currentPage?: unknown }).currentPage;
                const currentPage = typeof currentPageRaw === "number"
                  ? currentPageRaw
                  : typeof currentPageRaw === "string"
                    ? Number(currentPageRaw)
                    : null;
                const heartbeatAt = typeof (metadata as { heartbeatAt?: unknown }).heartbeatAt === "string"
                  ? String((metadata as { heartbeatAt?: unknown }).heartbeatAt)
                  : null;
                const heartbeatLabel = formatHeartbeat(heartbeatAt ?? undefined);
                const isStopping = abortRequested || run.status === "stopping";
                const statusLabel = isStopping && run.status === "running" ? "stopping" : run.status;
                const isRunning = run.status === "running" || run.status === "stopping";
                const metaParts = [
                  phase ? `phase: ${phase}` : null,
                  currentCategory ? `category: ${currentCategory}` : null,
                  Number.isFinite(currentPage ?? NaN) ? `page: ${currentPage}` : null,
                  currentSort ? `sort: ${currentSort}` : null,
                  heartbeatLabel ? `heartbeat: ${heartbeatLabel}` : null,
                  (isStopping && isRunning) ? "stop requested" : null
                ].filter((item): item is string => Boolean(item));
                const metaLine = metaParts.length > 0 ? metaParts.join(" | ") : null;

                return (
                  <div
                    key={run.id}
                    className={`card job-console-run-card ${(isRunning || abortRequested) ? "job-console-run-card--running" : ""}`}
                  >
                    <div className="job-console-run-header">
                      <p className="card-title">
                        {run.runType} | {statusLabel}
                      </p>
                      {isRunning && (
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm job-console-stop-btn"
                          onClick={() => stopJob(run.id)}
                          title="Stop this job"
                          disabled={isStopping}
                        >
                          {isStopping ? "Stopping..." : "Stop"}
                        </button>
                      )}
                    </div>
                    <p className="card-desc">
                      seen {run.startupsSeen} | inserted {run.startupsInserted} | updated {run.startupsUpdated}
                      {run.versionsInserted > 0 && ` | versions +${run.versionsInserted}`}
                    </p>
                    {metaLine && <p className="job-console-meta">{metaLine}</p>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
